import { useState } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { cn } from "@/lib/utils";
import { LockReason, lockedNoteOf } from "@buildpy/core/modules/quote/lock-reason";
import { MissingSuggestion } from "@buildpy/core/modules/quote/missing-rules";

interface Props {
  suggestions: MissingSuggestion[];
  ignoredCount: number;
  canAdd: (suggestion: MissingSuggestion) => boolean;
  onAdd: (suggestion: MissingSuggestion) => void;
  onIgnore: (id: string) => void;
  onResetIgnored: () => void;
  // 잠긴 까닭 — 있으면 「추가」 만 끈다(칩 펼치기·무시·무시 해제는 이 브라우저 보기 설정이라 그대로, 그래서 편집기는 이 패널을 disabled fieldset 밖에 둔다).
  lockReason?: LockReason | null;
}

/** 편집기 전용 「빠진 항목 n」 칩 — 눌러야 목록이 펼쳐진다. 고객 링크·출력물에는 없다. */
const MissingPanel = ({ suggestions, ignoredCount, canAdd, onAdd, onIgnore, onResetIgnored, lockReason = null }: Props) => {
  const [open, setOpen] = useState(false);
  if (suggestions.length === 0 && ignoredCount === 0) return null;
  const high = suggestions.some((s) => s.severity === "high");

  return (
    <div className="flex flex-col gap-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        {suggestions.length > 0 && (
          <button
            aria-expanded={open}
            className="inline-flex items-center gap-x-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs text-muted-foreground hover:bg-muted"
            type="button"
            onClick={() => setOpen((v) => !v)}>
            <span
              aria-hidden
              className={cn("size-1.5 rounded-full", high ? "bg-warning" : "bg-muted-foreground/60")}
            />
            빠진 항목 {suggestions.length} {open ? "▾" : "▸"}
          </button>
        )}
        {ignoredCount > 0 && (open || suggestions.length === 0) && (
          <Button
            size="xs"
            type="button"
            variant="link-muted"
            onClick={onResetIgnored}>
            무시한 {ignoredCount}개 다시 보기
          </Button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <SectionCard as="ul" className="flex flex-col" padding="none">
          {suggestions.map((s) => {
            const addable = canAdd(s);
            return (
              <li
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-1.5 text-sm last:border-0">
                <span className="flex flex-col">
                  <span>{s.message}</span>
                  <span className="text-xs text-muted-foreground">
                    제안: {s.suggestion.name} · {s.suggestion.unit}
                    {s.suggestion.qty !== undefined ? ` · ${s.suggestion.qty}` : ""}
                  </span>
                </span>
                <span className="flex items-center gap-x-1">
                  <Button
                    disabled={!addable || lockReason !== null}
                    size="sm"
                    title={lockReason !== null ? lockedNoteOf(lockReason) : addable ? "해당 공종 끝에 단가 0 으로 한 줄 추가합니다" : "숨긴 공종이라 추가할 수 없습니다"}
                    variant="outline"
                    onClick={() => onAdd(s)}>
                    추가
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onIgnore(s.id)}>
                    무시
                  </Button>
                </span>
              </li>
            );
          })}
        </SectionCard>
      )}
    </div>
  );
};

export default MissingPanel;
